import React, { useMemo } from 'react';
import { useAppStore } from '@/lib/store';

export default function OverviewView() {
  const { setActiveView, conversations, calendarEvents, memories, contextFiles } = useAppStore();
  const go = (view) => setActiveView(view);

  const upcoming = useMemo(() =>
    [...calendarEvents]
      .sort((a, b) => a.startTime - b.startTime)
      .filter((e) => e.startTime > Date.now() - 3_600_000)
      .slice(0, 5),
    [calendarEvents]
  );

  return (
    <div className="w-full h-full overflow-y-auto bg-[#232426]" style={{ color: '#d5d6d9' }}>

      {/* Hero — centered */}
      <section className="flex flex-col items-center justify-center px-6 pt-16 pb-12 text-center">
        <div className="text-[11px] font-semibold text-[#8f9196] tracking-widest uppercase mb-5 border border-[#515153] rounded-full px-4 py-1.5">
          Your personal AI workspace
        </div>
        <h1 className="text-white font-bold text-[28px] md:text-[42px] leading-tight mb-4 max-w-2xl">
          One AI. All your work.<br />
          <span className="text-[#F6661E]">Actually</span> remembered.
        </h1>
        <p className="text-[#8f9196] text-[14px] md:text-[16px] max-w-lg mb-8 leading-relaxed">
          Chat with 9 AI providers, schedule events naturally, upload context files, and let Palinos build a memory of you — across every session.
        </p>
        <div className="flex gap-3 flex-wrap justify-center mb-10">
          <button
            onClick={() => go('chats')}
            className="px-6 py-2.5 bg-[#6498CC] hover:bg-[#5282b8] text-white rounded-md font-semibold text-[14px] transition-colors"
          >
            Start Chatting
          </button>
          <button
            onClick={() => go('calendar')}
            className="px-6 py-2.5 border border-[#515153] hover:bg-[#323335] text-[#d5d6d9] rounded-md text-[14px] transition-colors"
          >
            Calendar
          </button>
          <button
            onClick={() => go('customise')}
            className="px-6 py-2.5 border border-[#515153] hover:bg-[#323335] text-[#d5d6d9] rounded-md text-[14px] transition-colors"
          >
            Add API Keys
          </button>
        </div>

        {/* Stats row */}
        <div className="flex flex-wrap items-center justify-center gap-8 md:gap-14 px-8 py-4 bg-[#323335] border border-[#515153] rounded-2xl max-w-xl w-full">
          {[
            { v: String(conversations.length || '0'), l: 'Conversations' },
            { v: String(memories.length || '0'),      l: 'Memories'      },
            { v: String(calendarEvents.length || '0'), l: 'Events'        },
            { v: String(contextFiles.length || '0'),  l: 'Context Files' },
          ].map((s) => (
            <div key={s.l} className="flex flex-col items-center">
              <span className="text-white font-bold text-[22px] tabular-nums">{s.v}</span>
              <span className="text-[#8f9196] text-[11px] mt-0.5">{s.l}</span>
            </div>
          ))}
        </div>
      </section>

      {/* 4 panels */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4 px-6 pb-12 max-w-5xl mx-auto w-full">

        {/* Recent Conversations */}
        <div className="bg-[#323335] border border-[#515153] rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <span className="text-white font-semibold text-[14px]">Recent Conversations</span>
            <button onClick={() => go('chats')} className="text-[#6498CC] text-[12px] hover:underline">View all</button>
          </div>
          {conversations.length === 0 ? (
            <p className="text-[#515153] text-[13px] py-4 text-center">No conversations yet.</p>
          ) : conversations.slice(0, 5).map((c) => (
            <button
              key={c._id ?? c.id}
              onClick={() => go('chats')}
              className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#3e3f42] transition-colors flex items-center gap-2.5 mb-1"
            >
              <span className="text-[#6498CC] text-[11px] flex-shrink-0">▶</span>
              <span className="text-[#d5d6d9] text-[13px] truncate flex-1">{c.title || 'Untitled'}</span>
              <span className="text-[#515153] text-[11px] flex-shrink-0">
                {new Date(c.updatedAt || c.createdAt || 0).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}
              </span>
            </button>
          ))}
        </div>

        {/* Upcoming Events */}
        <div className="bg-[#323335] border border-[#515153] rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <span className="text-white font-semibold text-[14px]">Upcoming Events</span>
            <button onClick={() => go('calendar')} className="text-[#6498CC] text-[12px] hover:underline">Calendar</button>
          </div>
          {upcoming.length === 0 ? (
            <p className="text-[#515153] text-[13px] py-4 text-center">No upcoming events. Tell Palinos to schedule one.</p>
          ) : upcoming.map((ev) => (
            <div key={ev.id ?? ev._id} className="flex items-start gap-3 px-3 py-2.5 rounded-lg bg-[#3e3f42] mb-2">
              <div className="w-2 h-2 rounded-full bg-[#6498CC] flex-shrink-0 mt-1.5" />
              <div className="min-w-0 flex-1">
                <div className="text-[#d5d6d9] text-[13px] font-medium truncate">{ev.title}</div>
                <div className="text-[#8f9196] text-[11px] mt-0.5">
                  {new Intl.DateTimeFormat('en-IN', { timeZone: 'Asia/Kolkata', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(ev.startTime))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Recent Memories */}
        <div className="bg-[#323335] border border-[#515153] rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <span className="text-white font-semibold text-[14px]">Recent Memories</span>
            <button onClick={() => go('customise')} className="text-[#6498CC] text-[12px] hover:underline">Manage</button>
          </div>
          {memories.length === 0 ? (
            <p className="text-[#515153] text-[13px] py-4 text-center">No memories yet — chat to build context.</p>
          ) : memories.slice(-5).reverse().map((m, i) => {
            const catColor = {
              name: 'text-[#6498CC]', role: 'text-[#71e184]',
              location: 'text-[#F6661E]', project: 'text-[#b07ae8]',
            }[m.category] ?? 'text-[#8f9196]';
            return (
              <div key={m.id ?? i} className="flex items-start gap-2 px-3 py-2 rounded-lg bg-[#3e3f42] mb-2">
                <span className={`text-[9px] font-bold uppercase flex-shrink-0 mt-1 ${catColor}`}>{m.category}</span>
                <span className="text-[#d5d6d9] text-[12px] leading-relaxed">
                  {(m.content || '').slice(0, 65)}{m.content?.length > 65 ? '…' : ''}
                </span>
              </div>
            );
          })}
        </div>

        {/* Context Files */}
        <div className="bg-[#323335] border border-[#515153] rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <span className="text-white font-semibold text-[14px]">Context Files</span>
            <button onClick={() => go('customise')} className="text-[#6498CC] text-[12px] hover:underline">Manage</button>
          </div>
          {contextFiles.length === 0 ? (
            <p className="text-[#515153] text-[13px] py-4 text-center">No context files yet. Upload one in Customise.</p>
          ) : (
            <div className="space-y-2">
              {contextFiles.slice(0, 6).map((f, i) => (
                <div key={i} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-[#3e3f42]">
                  <div className="w-7 h-7 rounded-md bg-[#232426] flex items-center justify-center flex-shrink-0">
                    <span className="text-[#6498CC] text-[10px] font-bold uppercase">{(f.type || 'txt').split('/').pop().slice(0, 3)}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[#d5d6d9] text-[13px] truncate">{f.name}</div>
                    {(f.wordCount || f.charCount) && (
                      <div className="text-[#515153] text-[10px] mt-0.5">
                        {f.wordCount ? `${f.wordCount} words` : `${f.charCount} chars`}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {contextFiles.length > 6 && (
                <div className="text-[#515153] text-[11px] text-center pt-1">+{contextFiles.length - 6} more files</div>
              )}
            </div>
          )}
        </div>

      </section>
    </div>
  );
}
